/**
 * Locale resolution for the web shell, mirroring the server precedence (trilingual spec §12; ADR-057):
 * `ui_locales` query → `dstbfs_locale` cookie → navigator languages → default `zh-CN`.
 * Locale only decides display; it never participates in identity, scope, permission or tenant decisions.
 */
export const SUPPORTED_LOCALES = ['zh-CN', 'en-US', 'ru-RU'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'zh-CN'
export const LOCALE_COOKIE = 'dstbfs_locale'

/** Normalises a BCP-47 candidate (`en`, `EN-us`, `ru`) to a supported locale, or null. */
export function normalizeLocale(candidate: string | null | undefined): Locale | null {
  if (!candidate) return null
  const lower = candidate.trim().toLowerCase()
  if (lower === '') return null
  const primary = lower.split('-')[0]
  for (const l of SUPPORTED_LOCALES) {
    if (l.toLowerCase() === lower) return l
  }
  for (const l of SUPPORTED_LOCALES) {
    if (l.toLowerCase().split('-')[0] === primary) return l
  }
  return null
}

export function resolveLocale(input: { query?: string | null; cookie?: string | null; navigatorLanguages?: readonly string[] }): Locale {
  const fromQuery = input.query?.split(' ').map(normalizeLocale).find((l): l is Locale => l !== null)
  if (fromQuery) return fromQuery
  const fromCookie = normalizeLocale(input.cookie)
  if (fromCookie) return fromCookie
  for (const lang of input.navigatorLanguages ?? []) {
    const l = normalizeLocale(lang)
    if (l) return l
  }
  return DEFAULT_LOCALE
}

export function readCookie(name: string, cookieString: string): string | null {
  for (const part of cookieString.split(';')) {
    const [k, ...rest] = part.trim().split('=')
    if (k === name) return decodeURIComponent(rest.join('='))
  }
  return null
}
